import { App } from 'App'
import { isEqual } from 'lodash'
import { createRoot } from 'react-dom/client'

import { getCurrentPage } from '@subaio/components'
import {
	type ApiOptions,
	apiCreate,
	delay,
	eventLoggerCreate,
	initErrorClient,
	logError,
	routerCreate,
	tokenRenew,
} from '@subaio/utils'
import { ContainerStatus, getRoutes } from '@subaio/webapp/src/containers'
import { getContainers } from '@subaio/webapp/src/containers/containers'

import { config } from 'clientConfig'

import type { Container } from 'lib/container'
import { storeCreate } from 'lib/store'

// Do NOT reuse this anywhere, requires an expensive license and is badly coded
import '@iframe-resizer/child'

import { abnTrackingCreate } from 'lib/abnTracking'

declare global {
	interface Window {
		parentIframe?: {
			scrollToOffset: (x: number, y: number) => void
		}
	}
}

initErrorClient(
	{
		enabled: true,
		dataSourceName: config.sentry.dataSourceName,
		release: config.version,
		environment: config.environment,
	},
	() => {
		const {
			page: { name, params, query },
		} = store.getState()

		return {
			user: undefined,
			view: { name, params, query },
		}
	},
)

const store = storeCreate()

if (config.isDev) {
	;(window as any).store = store
}

const apiShouldRetry: ApiOptions['shouldRetry'] = async (error, attempts) => {
	// If the request failed because of a network problem, retry (max 3 times)
	if (error.request.statusCode === 0) {
		// Wait one second longer after each attempt
		await delay(attempts)
		return true
	}

	return false
}

const ownApi = apiCreate({
	baseUrl: '/api',
	getLocation: () => store.getState().page.name,
	logRequestData: config.isDev,
	shouldRetry: apiShouldRetry,
})

const clientApi = apiCreate({
	baseUrl: config.api.client,
	getLocation: () => store.getState().page.name,
	getToken: () => {
		const { userAuth } = store.getState()
		if (userAuth.status !== 'authenticated') throw new Error('Cannot make unauthenticated calls')

		return userAuth.token
	},

	logRequestData: config.isDev,
	shouldRetry: apiShouldRetry,
})

const tokenPromise = ownApi
	.post<{ token: string; refreshToken: string }>('/users', { locale: config.locale })
	.then(({ token, refreshToken }) => {
		store.set({ userAuth: { status: 'authenticated', token, refreshToken } })
	})
	.catch((error) => {
		logError(error)
		store.set({ status: ContainerStatus.Errored })
	})

import(`locales/${config.locale}.yaml`)
	.then(({ default: messages }) => {
		store.set({ translation: { locale: config.locale, messages } })
	})
	.catch((error) => {
		logError(error)
		store.set({ status: ContainerStatus.Errored })
	})

tokenRenew({
	getToken: () => {
		const { userAuth } = store.getState()
		if (userAuth.status !== 'authenticated') return undefined

		return userAuth.token
	},
	onRenew: async () => {
		const { userAuth } = store.getState()
		if (userAuth.status !== 'authenticated') return undefined

		try {
			const { token, refreshToken } = await ownApi.post<{ token: string; refreshToken: string }>(
				'/users/refresh-token',
				{
					token: userAuth.token,
					refreshToken: userAuth.refreshToken,
				},
			)

			store.set({ userAuth: { status: 'authenticated', token, refreshToken } })
		} catch {
			// Suppress error
		}
	},
	onSignOut: () => {
		store.set({ userAuth: { status: 'expired' } })
	},
})

const eventLogger = eventLoggerCreate({
	enabled: config.eventLog.enabled,
	clientApiEndpoint: config.api.client,
	projectId: config.projectId,
	getToken: async () => {
		await tokenPromise

		const { userAuth } = store.getState()
		if (userAuth.status !== 'authenticated') throw new Error('Failed to get token for event logger')

		return userAuth.token
	},
	userDataWhitelist: ['serviceId', 'page', 'notificationType', 'notificationId'],
	// Allow this app to log noisy events for ABN only
	shouldLog: ({ type, data }) => type !== 'event' || !('externalOnly' in data),
	onLog: (event) => {
		console.log(event.type, event.identifier, event.data)
	},
})

const abnTracking = abnTrackingCreate({
	environment: config.environment,
	version: config.version,
})

const root = createRoot(document.getElementById('root')!)

const render = () => {
	const containers = getContainers<Container>()
	const router = routerCreate(getRoutes(containers))
	const currentPage = getCurrentPage(router)
	const { page, status } = store.getState()
	if (currentPage && (!isEqual(page, currentPage) || status === ContainerStatus.Errored)) {
		store.set({ page: currentPage, status: ContainerStatus.Initializing, data: {} })
	}
	// Render app
	root.render(
		<App
			containers={containers}
			store={store}
			router={router}
			clientApi={clientApi}
			eventLogger={eventLogger}
			abnTracking={abnTracking}
		/>,
	)
}

render()

// Hot Module Replacement API
if (module.hot) {
	module.hot.accept(['App', '@subaio/webapp/src/containers/containers'], () => {
		render()
	})
}
